import { ArrowRight, Flame, Lightbulb, Play, Target } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ImageWithFallback } from '@/components/figma/ImageWithFallback';
import { cn } from '@/components/ui/utils';
import { opportunityFor, type Trend } from '@/utils/trend';

/** Extract @handle from a TikTok share URL like https://www.tiktok.com/@user/video/123 */
function tiktokAuthor(shareUrl: string | null | undefined): string | null {
  if (!shareUrl) return null;
  const m = shareUrl.match(/tiktok\.com\/@([^/?]+)/i);
  return m ? `@${m[1]}` : null;
}

const STATUS_STYLES: Record<Trend['status'], { bg: string; fg: string; label: string }> = {
  emerging: { bg: 'bg-emerald-100', fg: 'text-emerald-700', label: 'Emerging' },
  active: { bg: 'bg-sky-100', fg: 'text-sky-700', label: 'Active' },
  peaking: { bg: 'bg-[#f3ecff]', fg: 'text-[#6b1f94]', label: 'Peaking' },
  declining: { bg: 'bg-gray-100', fg: 'text-gray-600', label: 'Declining' },
};

const OPP_STYLES: Record<'high' | 'medium' | 'low', string> = {
  high: 'bg-[#d9f99d] text-[#1c3a00] border-[#bef264]',
  medium: 'bg-orange-100 text-orange-700 border-orange-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

export interface TrendCardProps {
  trend: Trend;
  onOpen?: (trend: Trend) => void;
  onApply?: (trend: Trend) => void;
}

export function TrendCard({ trend, onOpen, onApply }: TrendCardProps) {
  const opp = opportunityFor(trend);
  const statusStyle = STATUS_STYLES[trend.status];
  const sparkline = trend.snapshots.map((s) => ({ day: s.snapshot_date, score: s.virality_score }));

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(trend)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen?.(trend);
      }}
      className="group flex min-h-[260px] cursor-pointer flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm transition-all hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#be43ff]/40 sm:flex-row"
    >
      <div className="relative h-[240px] flex-shrink-0 overflow-hidden bg-black sm:h-auto sm:w-[220px]">
        <ImageWithFallback
          src={trend.metadata.cover_image_url ?? ''}
          alt={trend.title}
          className={cn(
            'h-full w-full transition-transform duration-500 group-hover:scale-105',
            // TikTok thumbnails are portrait (9:16); use object-cover so the face/subject stays centered.
            trend.metadata.cover_source === 'tiktok' ? 'object-cover' : 'object-cover'
          )}
        />
        {trend.metadata.cover_source === 'tiktok' && trend.metadata.cover_video_url && (
          <>
            {/* Play overlay — cues "this is a video, tap for full experience" without autoplay noise in the grid. */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-gradient-to-b from-transparent via-black/0 to-black/55">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform group-hover:scale-110">
                <Play className="ml-0.5 h-6 w-6 fill-[#2e024c] text-[#2e024c]" />
              </div>
            </div>
            {/* Provenance: TikTok logo-text + author handle. */}
            <div className="pointer-events-none absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2">
              <span className="rounded-md bg-black/75 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
                TikTok
              </span>
              {tiktokAuthor(trend.metadata.cover_video_url) && (
                <span className="truncate rounded-md bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white/95 backdrop-blur-sm">
                  {tiktokAuthor(trend.metadata.cover_video_url)}
                </span>
              )}
            </div>
          </>
        )}
        <div className="absolute left-3 top-3 flex flex-col gap-1">
          <Badge className={cn('border-0 backdrop-blur-sm', statusStyle.bg, statusStyle.fg)}>
            {statusStyle.label}
          </Badge>
          <Badge className="border-0 bg-white/90 text-[10px] font-bold uppercase tracking-wider text-gray-900 backdrop-blur-sm">
            {trend.category}
          </Badge>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start gap-2">
          <h3 className="flex-1 text-[17px] font-bold text-[#101828] transition-colors group-hover:text-[#be43ff]">
            {trend.title}
          </h3>
          <Badge className={cn('border text-[10px]', OPP_STYLES[opp])}>
            {opp.toUpperCase()} OP
          </Badge>
        </div>
        <p className="mt-2 line-clamp-3 text-[13px] leading-relaxed text-gray-600">
          {trend.description}
        </p>

        <div className="mt-3 h-[44px] w-full">
          <ResponsiveContainer>
            <AreaChart data={sparkline}>
              <defs>
                <linearGradient id={`spark-${trend.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#be43ff" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#be43ff" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="score"
                stroke="#be43ff"
                strokeWidth={1.75}
                fill={`url(#spark-${trend.id})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-auto flex items-center gap-4 border-t border-gray-50 pt-3">
          <div className="flex items-center gap-1.5">
            <Flame className="h-3.5 w-3.5 text-orange-500" />
            <span className="text-[12px] font-semibold text-gray-900">{trend.virality_score}</span>
            <span className="text-[10px] font-medium text-gray-500">Viral</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5 text-[#be43ff]" />
            <span className="text-[12px] font-semibold text-gray-900">
              {trend.relevance.relevance_score}
            </span>
            <span className="text-[10px] font-medium text-gray-500">Match</span>
          </div>
          <span className="ml-auto truncate text-[10px] font-medium italic text-gray-400">
            via {trend.signals.length} signal{trend.signals.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      <div className="flex flex-col justify-center gap-3 border-t border-gray-100 bg-[#f3ecff] p-5 sm:w-[220px] sm:border-l sm:border-t-0">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#e5d4ff]">
            <Lightbulb className="h-3.5 w-3.5 text-[#be43ff]" />
          </div>
          <span className="text-[11px] font-bold uppercase tracking-tight text-[#2e024c]">
            For you
          </span>
        </div>
        <p className="text-[13px] font-medium italic leading-snug text-[#2e024c]/80">
          "{trend.relevance.suggested_action}"
        </p>
        <Button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onApply?.(trend);
          }}
          className="mt-1 h-9 w-full gap-2 rounded-xl bg-[#2e024c] text-xs text-white shadow-md shadow-purple-200 transition-all hover:bg-[#3a0d5e]"
        >
          Tap into this
          <ArrowRight className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
